import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Sync as SyncIcon,
  CloudSync as CloudSyncIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import whatsappTemplateService, { WhatsAppTemplate, TemplateComponents } from '../../services/whatsappTemplateService';

function getBodyPreview(t: WhatsAppTemplate): string {
  const c = t.components as TemplateComponents | undefined;
  if (c?.body?.text) {
    return c.body.text;
  }
  return t.content || '';
}

function componentBadges(t: WhatsAppTemplate) {
  const c = t.components as TemplateComponents | undefined;
  if (!c) {
    return <Chip size="small" label="legacy" variant="outlined" />;
  }
  const out: string[] = [];
  if (c.header?.enabled && c.header.text) {
    out.push('H');
  }
  if (c.body?.text) {
    out.push('B');
  }
  if (c.footer?.enabled && c.footer.text) {
    out.push('F');
  }
  const bcount = c.buttons?.length || 0;
  if (bcount) {
    out.push(`Btn×${bcount}`);
  }
  if (out.length === 0) {
    return <Chip size="small" label="—" variant="outlined" />;
  }
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
      {out.map((x) => (
        <Chip key={x} size="small" label={x} color="default" />
      ))}
    </Stack>
  );
}

const WhatsAppTemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncingAll, setSyncingAll] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<WhatsAppTemplate | null>(null);

  const navigate = useNavigate();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappTemplateService.getAll();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load WhatsApp templates. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchTemplates();
  }, []);

  const handleCreateNew = () => {
    navigate('/whatsapp-templates/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/whatsapp-templates/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/whatsapp-templates/view/${id}`);
  };

  const handleDeleteClick = (template: WhatsAppTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete || !templateToDelete.id) return;

    try {
      await whatsappTemplateService.delete(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError('Failed to delete WhatsApp template. Please try again later.');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleSyncStatus = async (id: number) => {
    try {
      setError(null);
      await whatsappTemplateService.syncStatus(id);
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to sync template status:', err);
      setError('Failed to sync template status from Meta.');
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      setError(null);
      const res = await whatsappTemplateService.syncAll();
      if (res.data?.errors?.length) {
        setError(
          `Some templates failed to sync (${res.data.errors.length}). ` +
            `Updated ${res.data.updated} successfully.`
        );
      }
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to sync all:', err);
      setError('Failed to sync all templates from Meta.');
    } finally {
      setSyncingAll(false);
    }
  };

  const getStatusColor = (status: string) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'APPROVED':
        return 'success' as const;
      case 'PENDING':
        return 'warning' as const;
      case 'REJECTED':
        return 'error' as const;
      default:
        return 'default' as const;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h1">
          WhatsApp Templates
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={syncingAll ? <CircularProgress size={18} color="inherit" /> : <CloudSyncIcon />}
            onClick={() => void handleSyncAll()}
            disabled={syncingAll}
          >
            Sync all from Meta
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreateNew}>
            Create new template
          </Button>
        </Stack>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Meta name / ID</TableCell>
              <TableCell>Format</TableCell>
              <TableCell>Components</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Body preview</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No WhatsApp templates found. Create a new template to get started.
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{template.meta_template_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.meta_template_id || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={template.parameter_format || 'POSITIONAL'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{componentBadges(template)}</TableCell>
                  <TableCell>{template.template_category || '—'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getBodyPreview(template)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={String(template.status).toUpperCase()}
                      color={getStatusColor(template.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {template.updated_at
                      ? new Date(template.updated_at).toLocaleString()
                      : template.created_at
                        ? new Date(template.created_at).toLocaleString()
                        : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton color="info" onClick={() => handleView(template.id!)} size="small" aria-label="view">
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(template.id!)}
                        size="small"
                        aria-label="edit"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(template)}
                        size="small"
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sync this template from Meta">
                      <IconButton
                        color="secondary"
                        onClick={() => void handleSyncStatus(template.id!)}
                        size="small"
                        aria-label="sync"
                      >
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete WhatsApp template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template &quot;{templateToDelete?.name}&quot;? This only removes the CRM
            record, not the template in Meta.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppTemplateList;
